import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, RefreshCw, Bug, ChevronRight, AlertTriangle, Gamepad2, Cpu, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import { SeverityBadge, StatusBadge } from "./Dashboard";
import { API } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { toast } from "sonner";

export default function BugList() {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get("search") || "");
  const [fetchError, setFetchError] = useState(false);
  const [severity, setSeverity] = useState(searchParams.get("severity") || "");
  const [classification, setClassification] = useState(searchParams.get("classification") || "");
  const [status, setStatus] = useState(searchParams.get("status") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [game, setGame] = useState(searchParams.get("game") || "");
  const [classifications, setClassifications] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [categories, setCategories] = useState([]);

  const fetchOptions = async () => {
    try {
      const [classRes, sevRes, statRes, catRes] = await Promise.all([
        axios.get(`${API}/classifications`),
        axios.get(`${API}/severities`),
        axios.get(`${API}/statuses`),
        axios.get(`${API}/categories`),
      ]);
      setClassifications(classRes.data);
      setSeverities(sevRes.data);
      setStatuses(statRes.data);
      // Categories API now returns object with types, extract keys as array
      setCategories(Object.keys(catRes.data));
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  const fetchBugs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (severity && severity !== "all") params.append("severity", severity);
      if (classification && classification !== "all") params.append("classification", classification);
      if (status && status !== "all") params.append("status", status);
      if (category && category !== "all") params.append("category", category);
      if (game && game !== "all") params.append("game", game);
      
      const response = await axios.get(`${API}/bugs?${params.toString()}`);
      setBugs(response.data);
      setFetchError(false);
    } catch (error) {
      console.error("Error fetching bugs:", error);
      setFetchError(true);
      if (error.response?.status === 429) {
        toast.error("Too many requests — please wait a moment and retry.");
      } else {
        toast.error("Failed to load bug reports.");
      }
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, severity, classification, status, category, game]);

  // Initialize filters from URL on mount
  useEffect(() => {
    fetchOptions();
  }, []);

  // Debounce search input (avoid one API request per keystroke)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 450);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch bugs when filters change
  useEffect(() => {
    fetchBugs();
    
    // Update URL params
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (severity && severity !== "all") params.set("severity", severity);
    if (classification && classification !== "all") params.set("classification", classification);
    if (status && status !== "all") params.set("status", status);
    if (category && category !== "all") params.set("category", category);
    if (game && game !== "all") params.set("game", game);
    setSearchParams(params, { replace: true });
  }, [fetchBugs, setSearchParams, debouncedSearch, severity, classification, status, category, game]);

  const handleSearch = (e) => {
    e.preventDefault();
    setDebouncedSearch(search);
  };

  const clearFilters = () => {
    setSearch("");
    setSeverity("");
    setClassification("");
    setStatus("");
    setCategory("");
    setGame("");
    setSearchParams({}, { replace: true });
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in" data-testid="bug-list-page">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-widest text-foreground">{t('bugList.title')}</h1>
        <div className="cyber-line mt-2" />
        <p className="text-muted-foreground mt-2 font-body text-sm md:text-base">{t('bugList.subtitle')}</p>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border/40 mb-4 md:mb-6">
        <CardContent className="p-3 md:p-4">
          <form onSubmit={handleSearch} className="space-y-3">
            {/* Search Row */}
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">{t('bugList.search')}</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by title, description, or display code..."
                    className="pl-10 bg-black/20 border-border/50 focus:border-primary font-mono"
                    data-testid="search-input"
                  />
                </div>
              </div>
            </div>

            {/* Filter Row */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-3">
              <div>
                <label className="text-[10px] md:text-xs text-muted-foreground font-mono uppercase tracking-wider mb-1 block flex items-center gap-1">
                  <Cpu className="w-3 h-3" />
                  Game
                </label>
                <Select value={game} onValueChange={setGame}>
                  <SelectTrigger className="bg-black/20 border-border/50 font-mono text-xs" data-testid="game-filter">
                    <SelectValue placeholder={t('bugList.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('bugList.all')}</SelectItem>
                    <SelectItem value="STAR_ATLAS_UE5">
                      <div className="flex items-center gap-2">
                        <Cpu className="w-3 h-3" />
                        UE5
                      </div>
                    </SelectItem>
                    <SelectItem value="STAR_ATLAS_HOLOSIM">
                      <div className="flex items-center gap-2">
                        <Globe className="w-3 h-3" />
                        Holosim
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-[10px] md:text-xs text-muted-foreground font-mono uppercase tracking-wider mb-1 block flex items-center gap-1">
                  <Gamepad2 className="w-3 h-3" />
                  Category
                </label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="bg-black/20 border-border/50 font-mono text-xs" data-testid="category-filter">
                    <SelectValue placeholder={t('bugList.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('bugList.all')}</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-[10px] md:text-xs text-muted-foreground font-mono uppercase tracking-wider mb-1 block">{t('bugList.severity')}</label>
                <Select value={severity} onValueChange={setSeverity}>
                  <SelectTrigger className="bg-black/20 border-border/50 font-mono text-xs" data-testid="severity-filter">
                    <SelectValue placeholder={t('bugList.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('bugList.all')}</SelectItem>
                    {severities.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-[10px] md:text-xs text-muted-foreground font-mono uppercase tracking-wider mb-1 block">{t('bugList.classification')}</label>
                <Select value={classification} onValueChange={setClassification}>
                  <SelectTrigger className="bg-black/20 border-border/50 font-mono text-xs" data-testid="classification-filter">
                    <SelectValue placeholder={t('bugList.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('bugList.all')}</SelectItem>
                    {classifications.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-[10px] md:text-xs text-muted-foreground font-mono uppercase tracking-wider mb-1 block">{t('bugList.status')}</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="bg-black/20 border-border/50 font-mono text-xs" data-testid="status-filter">
                    <SelectValue placeholder={t('bugList.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('bugList.all')}</SelectItem>
                    {statuses.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2 md:col-span-1 flex gap-2 items-end">
                <Button type="submit" className="flex-1 font-heading font-bold uppercase tracking-wider text-xs" data-testid="search-btn">
                  <Search className="w-4 h-4 mr-1" />
                  {t('bugList.searchBtn')}
                </Button>
                <Button type="button" variant="outline" onClick={clearFilters} className="font-heading text-xs px-2" data-testid="clear-filters-btn">
                  {t('bugList.clear')}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Bug List */}
      <Card className="bg-card border-border/40">
        <CardHeader className="border-b border-border/40 bg-muted/20 p-3 md:p-4">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-sm md:text-lg font-bold uppercase tracking-wider text-muted-foreground">
              {bugs.length} {t('bugList.bugsFound')}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchBugs} className="text-muted-foreground" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{t('bugList.refresh')}</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 flex items-center justify-center">
              <RefreshCw className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : fetchError ? (
            <div className="p-8 text-center" data-testid="bug-list-error">
              <AlertTriangle className="w-10 h-10 text-orange-400 mx-auto mb-3" />
              <p className="text-muted-foreground font-body mb-4">Could not load bug reports. The server may be busy.</p>
              <Button variant="outline" onClick={fetchBugs} className="font-heading text-xs" data-testid="bug-list-retry-btn">
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </div>
          ) : bugs.length > 0 ? (
            <div className="divide-y divide-border/40">
              {bugs.map((bug) => (
                <Link
                  key={bug.id}
                  to={`/bugs/${bug.id}`}
                  className="block p-3 md:p-4 hover:bg-muted/30 transition-colors group"
                  data-testid={`bug-item-${bug.id}`}
                >
                  <div className="flex items-start justify-between gap-2 md:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {/* Game Badge */}
                        {bug.game === "STAR_ATLAS_HOLOSIM" ? (
                          <span className="font-mono text-[9px] text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded-sm border border-cyan-500/20 flex items-center gap-1">
                            <Globe className="w-2.5 h-2.5" />
                            HOLOSIM
                          </span>
                        ) : (
                          <span className="font-mono text-[9px] text-primary bg-primary/10 px-1.5 py-0.5 rounded-sm border border-primary/20 flex items-center gap-1">
                            <Cpu className="w-2.5 h-2.5" />
                            UE5
                          </span>
                        )}
                        {/* Display Code */}
                        <span className="font-mono text-[10px] text-primary bg-primary/10 px-1.5 py-0.5 rounded-sm border border-primary/20">
                          {bug.display_code || `#${bug.id.slice(0, 8)}`}
                        </span>
                        <SeverityBadge severity={bug.severity} />
                        <StatusBadge status={bug.status} />
                        {bug.reopened && (
                          <span className="px-1.5 py-0.5 bg-orange-500/10 text-orange-400 border border-orange-500/20 rounded-none font-mono text-[10px] uppercase flex items-center gap-1">
                            <AlertTriangle className="w-2.5 h-2.5" />
                            Reopened
                          </span>
                        )}
                        {bug.duplicate_count > 0 && (
                          <span className="px-2 py-0.5 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded-none font-mono text-[10px] uppercase">
                            {bug.duplicate_count} {t('bugList.duplicates')}
                          </span>
                        )}
                      </div>
                      <h3 className="font-body text-sm md:text-base text-foreground group-hover:text-primary transition-colors line-clamp-2">
                        {bug.title}
                      </h3>
                      <p className="text-xs md:text-sm text-muted-foreground mt-1 line-clamp-2 hidden sm:block">
                        {bug.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 md:gap-4 mt-2 text-[10px] md:text-xs text-muted-foreground font-mono">
                        {bug.category && (
                          <span className="flex items-center gap-1">
                            <Gamepad2 className="w-3 h-3" />
                            {bug.category}
                          </span>
                        )}
                        <span>{bug.classification}</span>
                        <span className="hidden sm:inline">•</span>
                        <span className="hidden sm:inline">{bug.source}</span>
                        {bug.frequency && (
                          <>
                            <span className="hidden md:inline">•</span>
                            <span className="hidden md:inline">{bug.frequency}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Bug className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground font-mono text-sm">{t('bugList.noBugsFound')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
